import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Link } from "react-router-dom";

import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Flex,
  Label,
  Table,
  Button,
  Tooltip,
  TableRow,
  useModal,
  Explainer,
  NoResults,
  TableBody,
  TableCell,
  TableHead,
  TextInput,
  FilterMenu,
  Pagination,
  PageWrapper,
  StretchPanel,
  TableHeadCell,
  useNotification,
  useFilterReducer,
  usePaginationReducer,
  OrderableTableHeadCell,
  useOrderableTableReducer,
} from "@ubisend/pulse-components";
import Icon from "@ubisend/pulse-icons";

import { useAuth } from "../hooks";
import { refetchReports, requestExport as requestExportApi } from "../api";

dayjs.extend(relativeTime);

const Reporting = () => {
  const { showModal, hideModal } = useModal();
  const { showSuccess } = useNotification();
  const { user } = useAuth();
  const pagination = usePaginationReducer({ id: "reports" });
  const order = useOrderableTableReducer({ id: "reports" });

  const filters = useFilterReducer({
    id: "reports",
    initialFilters: { name: "" },
    options: { pagination },
  });

  const query = useQuery({
    queryKey: [
      "reports",
      { ...pagination.params, ...order.params, ...filters.form },
    ],
  });

  const handleNameChange = (event) => {
    const name = event.target.value;
    filters.updateFilters({ name });
  };

  const getLastFetched = () => {
    if (!query.data.data.length) {
      return "";
    }

    const lastUpdatedAt = query.data.data.reduce((mostRecent, item) =>
      new Date(item.last_updated) > new Date(mostRecent.last_updated)
        ? item
        : mostRecent,
    ).last_updated;

    return `Last Fetched: ${dayjs(lastUpdatedAt).from(dayjs())}`;
  };

  const { mutate: requestExport } = useMutation({
    mutationFn: requestExportApi,
  });

  const { mutate: requestRefetch } = useMutation({
    mutationFn: refetchReports,
  });

  const handleExport = () => {
    showModal({
      header: "Export reports to csv",
      body: `This will export a summary of all project reports that have been generated previously, including the most recent. The result will be emailed to you at ${user.email}.`,
      handleConfirm: async () => {
        try {
          const response = await requestExport({ id: user.id });

          if (response?.status === 200) {
            showSuccess(`Successfully requested reports export`);
          }
        } finally {
          hideModal();
        }
      },
    });
  };

  const handleRefetch = () => {
    showModal({
      header: "Refetch all reports",
      body: `This will mannually grab report data from each Pulse install configured with a client access token. This process could take some time.`,
      handleConfirm: async () => {
        try {
          const response = await requestRefetch();

          if (response?.status === 200) {
            showSuccess(`Successfully requested reports`);
          }
        } finally {
          hideModal();
        }
      },
    });
  };

  return (
    <PageWrapper
      header="Reporting"
      subheader={!query.isLoading && query.data ? getLastFetched() : ""}
      actions={
        <Flex xSpace>
          <FilterMenu
            buttonProps={{ loading: query.isLoading }}
            {...filters.props}
          >
            <Flex style={{ width: "20rem" }} col>
              <Flex col fat mb>
                <Label htmlFor="name">Name</Label>
                <TextInput
                  id="name"
                  placeholder="Project name"
                  value={filters.filters.name}
                  onChange={handleNameChange}
                />
              </Flex>
            </Flex>
          </FilterMenu>
          <Button
            aria-label="refetch-reports"
            icon="plus"
            onClick={handleRefetch}
          >
            Refetch
          </Button>
          <Button
            aria-label="request-export"
            icon="download"
            onClick={handleExport}
            variant="secondary"
          >
            Export
          </Button>
        </Flex>
      }
    >
      <Flex mb>
        <Explainer type="note">
          Reports will automatically refresh each day at midnight. Only
          installations with a configured client id and secret will be fetched,
          this needs to be entered manually on the Projects dashboard.
        </Explainer>
      </Flex>
      <StretchPanel>
        <Table loading={query.isLoading} loadingColumns={4}>
          {!query.isLoading && query.data && (
            <>
              {!query.data.data.length === 0 && <NoResults />}
              {query.data.data.length > 0 && (
                <TableHead>
                  <TableRow>
                    <OrderableTableHeadCell
                      row={{
                        label: "App name",
                        sort: "name",
                      }}
                      {...order.props}
                    />
                    <OrderableTableHeadCell
                      row={{
                        label: "Number of clients",
                        sort: "latest_client_total",
                      }}
                      {...order.props}
                    />
                    <OrderableTableHeadCell
                      row={{
                        label: "Number of users",
                        sort: "latest_user_total",
                      }}
                      {...order.props}
                    />
                    <TableHeadCell>Actions</TableHeadCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {query.data.data.map(
                  (
                    {
                      project_id,
                      project_name,
                      total_users,
                      total_clients,
                      reports_are_fetchable,
                    },
                    key,
                  ) => (
                    <TableRow key={key}>
                      <TableCell>
                        <Flex xSpace>
                          {!reports_are_fetchable && (
                            <Tooltip
                              position={Tooltip.POSITIONS.RIGHT}
                              tooltip={
                                <Tooltip.Content>
                                  Unable to fetch new reports for this project.
                                  Ensure the projects Client ID and Secret are
                                  set correctly
                                </Tooltip.Content>
                              }
                            >
                              <Icon
                                stroke="red"
                                size="1rem"
                                width="1rem"
                                height="1rem"
                                type="exclamation"
                                outline
                                style={{ cursor: "auto" }}
                              />
                            </Tooltip>
                          )}

                          <Flex mr>{project_name}</Flex>
                        </Flex>
                      </TableCell>
                      <TableCell>{total_clients}</TableCell>
                      <TableCell>{total_users}</TableCell>
                      <TableCell>
                        <Flex>
                          {project_id && (
                            <Button
                              as={Link}
                              icon="eye"
                              to={`/reports/${project_id}/report`}
                            >
                              <span aria-label={`view-users-${project_id}`}>
                                Info
                              </span>
                            </Button>
                          )}
                          {project_id && (
                            <Button
                              as={Link}
                              icon="chartBar"
                              ml="true"
                              to={`/reports/${project_id}/metrics`}
                            >
                              <span aria-label={`view-metrics-${project_id}`}>
                                Metrics
                              </span>
                            </Button>
                          )}
                          {!project_id && (
                            <Button
                              disabled
                              icon="exclamationCircle"
                              variant="secondary"
                            >
                              Unavailable
                            </Button>
                          )}
                        </Flex>
                      </TableCell>
                    </TableRow>
                  ),
                )}
              </TableBody>
            </>
          )}
        </Table>
        {query.data?.meta && (
          <Pagination pagination={query.data.meta} {...pagination.props} />
        )}
      </StretchPanel>
    </PageWrapper>
  );
};

export default Reporting;
